import { registerLicense, Internationalization } from "@syncfusion/ej2-base";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChevronDown } from "lucide-react";
import {
  ColumnDirective,
  ColumnsDirective,
  CommandColumn,
  GridComponent,
  Toolbar,
  Filter,
  ColumnMenu,
} from "@syncfusion/ej2-react-grids";
import { Inject, Page, Sort, Edit } from "@syncfusion/ej2-react-grids";
import { collection, getDocs, addDoc, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";
import { doc, getDoc, Query, updateDoc, setDoc } from "firebase/firestore";
import DisasterDashboard from "./center-pov/DisasterDeclaration/DisasterDashboard.js";
import { useAuth } from "./../components/AuthContext.js";
import Demographics from "./center-pov/Demographics.js";
import Dashboard from "./Dashboard.js";

// Syncfusion license key
registerLicense(
  "Ngo9BigBOggjHTQxAR8/V1NBaF5cXmZCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdnWXxceXRcQmZdV0R/XUM="
);

const Home = ({ className = "" }) => {
  const [userData, setUserData] = useState("");
  const [activeData, setActiveData] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [disasterNames, setDisasterNames] = useState([]);
  const [selectedDisaster, setSelectedDisaster] = useState("");
  const [selectedDisasterId, setSelectedDisasterId] = useState("");
  const sortSettings = {
    allowSorting: true,
  };
  const filterSettings = {
    type: "CheckBox",
  };
  const datepickerparams = {
    params: {
      value: new Date(),
      format: "MM/dd/yyyy",
      showClearButton: false,
      showTodayButton: true,
    },
  };
  const columnMenuItems = ["SortAscending", "SortDescending", "Filter"];
  const pageSettings = { pageSize: 10 };
  const selectedUserId = sessionStorage.getItem("userId");
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    const fetchUserData = async () => {
      if (user && selectedUserId) {
        const userDoc = await getDoc(doc(db, "users", selectedUserId));

        if (userDoc.exists()) {
          const role = userDoc.data().user_role;
          const userData = userDoc.data();
          setUserRole(role);

          let updatedUserData = userData;

          if (role === "center") {
            const centerDoc = await getDoc(
              doc(db, "centers", userData.center_id)
            );
            if (centerDoc.exists()) {
              updatedUserData = centerDoc.data();
            }
          } else if (role === "partner") {
            const partnerDoc = await getDoc(
              doc(db, "partners", userData?.partner_id)
            );
            if (partnerDoc.exists()) {
              updatedUserData = partnerDoc.data();
            }
          }

          setUserData(updatedUserData);

          try {
            let disasters = [];
            let activeData = [];

            if (role === "partner") {
              if (!userData?.center_id) {
                console.log("No center_id available for partner");
                setLoading(false);
                return;
              }

              const centerIds = Array.isArray(userData.center_id)
                ? userData.center_id
                : [userData.center_id];

              if (centerIds.length === 0) {
                console.log("No center IDs available");
                setLoading(false);
                return;
              }

              const counties = updatedUserData?.area_serviced_county || [];

              if (counties.length === 0) {
                console.log("No counties to query");
                setLoading(false);
                return;
              }

              const chunks = [];
              for (let i = 0; i < counties.length; i += 10) {
                chunks.push(counties.slice(i, i + 10));
              }

              const seenIds = new Set();

              for (const chunk of chunks) {
                const q = query(
                  collection(db, "declared-disaster"),
                  where("disaster_status", "==", "active"),
                  where("is_deleted", "==", false),
                  where("center_id", "in", centerIds),
                  where("affected_counties", "array-contains-any", chunk)
                );

                const snapshot = await getDocs(q);

                snapshot.forEach((doc) => {
                  if (!seenIds.has(doc.id)) {
                    seenIds.add(doc.id);
                    disasters.push({
                      id: doc.id,
                      name: doc.data().disaster_name,
                    });

                    activeData.push({
                      disaster_id: doc.id,
                      disaster_name: doc.data().disaster_name,
                      disaster_status: doc.data().disaster_status,
                      start_date: doc.data().start_date
                        ? doc
                            .data()
                            .start_date.toDate()
                            .toLocaleDateString("en-US", {
                              month: "2-digit",
                              day: "2-digit",
                              year: "numeric",
                            })
                        : null,
                      county: doc.data().affected_counties,
                    });
                  }
                });
              }
            } else if (role === "center") {
              if (!userData?.center_id) {
                console.log("No center_id available for center");
                setLoading(false);
                return;
              }

              const q = query(
                collection(db, "declared-disaster"),
                where("disaster_status", "==", "active"),
                where("is_deleted", "==", false),
                where("center_id", "==", userData.center_id),
                where(
                  "affected_counties",
                  "array-contains",
                  updatedUserData?.county
                )
              );

              const querySnapshot = await getDocs(q);

              disasters = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                name: doc.data().disaster_name,
              }));

              activeData = querySnapshot.docs.map((doc) => ({
                disaster_id: doc.id,
                disaster_name: doc.data().disaster_name,
                disaster_status: doc.data().disaster_status,
                start_date: doc.data().start_date
                  ? doc.data().start_date.toDate().toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                    })
                  : null,
                county: doc.data().affected_counties,
              }));
            }

            setDisasterNames(disasters);
            setActiveData(activeData);

            if (disasters.length > 0) {
              setSelectedDisaster(disasters[0].name);
              setSelectedDisasterId(disasters[0].id);
            }

            setLoading(false);
          } catch (error) {
            console.error("Error fetching disaster names:", error);
            setLoading(false);
          }
        }
      }
    };

    fetchUserData();
  }, [user, selectedUserId]);

  const handleDisasterChange = (e) => {
    const selectedName = e.target.value;
    setSelectedDisaster(selectedName);

    const selectedDisaster = disasterNames.find(
      (disaster) => disaster.name === selectedName
    );

    console.log(selectedDisaster);

    if (selectedDisaster) {
      setSelectedDisasterId(selectedDisaster.id);
      console.log(selectedDisaster.id);
    }
  };

  if (loading) {
    return <div className=""></div>;
  }

  return (
    <div
      className={`m-0 box-border flex w-[1648px] max-w-full flex-col items-start justify-start gap-2.5 px-2.5 py-[15px] leading-[normal] tracking-[normal] ${className}`}
    >
      {/* Report Generator Start */}
      {disasterNames.length > 0 ? (
        <div className="w-full flex-1 rounded-mini bg-white">
          <div
            className="fixed left-0 top-1 z-20 ml-[90vh] flex items-center justify-center gap-1 p-1"
            ref={dropdownRef}
          >
            <div className="relative w-[300px] cursor-pointer rounded px-2 hover:bg-slate-400">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="relative flex w-full items-center justify-between gap-1.5 bg-transparent px-0 py-2 text-[17px] text-white"
              >
                <span className="truncate">
                  {selectedDisaster ? selectedDisaster : "Select a Disaster"}
                </span>
                <ChevronDown className="h-4 w-4" strokeWidth={2} />
              </button>

              {isDropdownOpen && (
                <div className="absolute left-1/2 z-10 mt-1 min-w-[400px] -translate-x-1/2 rounded border border-gray-300 bg-white shadow-lg">
                  <div className="max-h-60 overflow-y-auto">
                    {disasterNames.map((disaster) => (
                      <div
                        key={disaster.id}
                        className="hover:bg-gray-100 cursor-pointer px-4 py-2"
                        onClick={() => {
                          setSelectedDisasterId(disaster.id);
                          setSelectedDisaster(disaster.name);
                          setIsDropdownOpen(false);
                        }}
                      >
                        {disaster.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="">
            {/* <DisasterDashboard
                propId={selectedDisasterId}
              /> */}
            <Dashboard propId={selectedDisasterId} />
          </div>
        </div>
      ) : userRole === "center" || userRole === "partner" ? (
        <div className="relative -mx-2.5 -my-[15px] overflow-hidden">
          <div className="-mt-10">
            <Demographics />
          </div>
        </div>
      ) : null}
      {/* Report Generator End */}
      {/* Active Disaster Grid Start */}
      {/* <section className="mt-4 w-full rounded-md border border-gray-200 bg-white pr-4">
        <h1 className="mb-3 text-base font-bold text-blue-500">
          ACTIVE DISASTERS
        </h1>

        <GridComponent
          dataSource={activeData}
          allowSorting={true}
          sortSettings={sortSettings}
          filterSettings={filterSettings}
          allowFiltering={true}
          allowPaging={true}
          pageSettings={pageSettings}
          showColumnMenu={true}
          columnMenuItems={columnMenuItems}
        >
          <ColumnsDirective>
            <ColumnDirective
              field="disaster_id"
              headerText="Disaster ID"
              isPrimaryKey={true}
              visible={false}
            />
            <ColumnDirective
              field="disaster_name"
              headerText="Disaster"
              width="150"
              textAlign="Left"
              editType="stringedit"
            />
            <ColumnDirective
              field="disaster_status"
              headerText="Status"
              width="100"
              textAlign="Left"
              editType="dropdownedit"
              edit={{
                params: {
                  dataSource: [
                    { text: "Active", value: "Active" },
                    { text: "Inactive", value: "Inactive" },
                  ],
                  fields: { text: "text", value: "value" },
                  query: new Query(),
                },
              }}
            />
            <ColumnDirective
              field="start_date"
              headerText="Date"
              width="100"
              format="MM/dd/yyyy"
              editType="datepickeredit"
              edit={datepickerparams}
            />
            <ColumnDirective
              field="county"
              headerText="Affected Counties"
              width="100"
              editType="stringedit"
            />
          </ColumnsDirective>
          <Inject
            services={[
              Page,
              Sort,
              Edit,
              CommandColumn,
              Toolbar,
              Filter,
              ColumnMenu,
            ]}
          />
        </GridComponent>
      </section> */}
      {/* Active Disaster Grid End */}
    </div>
  );
};

export default Home;
